// Customfly Premium Theme Core Scripts

class ThemeDrawer {
  constructor(drawerId, triggersSelector) {
    this.drawer = document.getElementById(drawerId);
    this.triggers = document.querySelectorAll(triggersSelector);
    this.overlay = document.querySelector('.drawer-overlay');
    this.closeBtn = this.drawer ? this.drawer.querySelector('.drawer-close') : null;

    if (!this.drawer) return;

    this.initEvents();
  }

  initEvents() {
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    // Keyboard Accessibility (ESC to close)
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('open')) {
        this.close();
      }
    });
  }

  open() {
    this.drawer.classList.add('open');
    if (this.overlay) {
      this.overlay.classList.add('active');
    }
    document.body.style.overflow = 'hidden';

    // If opening cart drawer, fetch fresh cart data
    if (this.drawer.id === 'cart-drawer' && window.cartDrawerManager) {
      window.cartDrawerManager.fetchCart();
    }
    
    // Focus close button for accessibility
    if (this.closeBtn) {
      setTimeout(() => this.closeBtn.focus(), 100);
    }
  }

  close() {
    this.drawer.classList.remove('open');
    if (this.overlay) {
      this.overlay.classList.remove('active');
    }
    document.body.style.overflow = '';
  }
}

// ─── Cart Drawer & AJAX Cart Manager ───
class CartDrawerManager {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.body = document.getElementById('CartDrawerBody');
    this.footer = document.getElementById('CartDrawerFooter');
    this.countEl = document.getElementById('CartDrawerCount');
    this.subtotalEl = document.getElementById('CartDrawerSubtotal');
    this.badgeEl = document.getElementById('CartCountBadge');

    this.init();
  }

  init() {
    this.bindGlobalFormSubmit();
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  async fetchCart() {
    if (!this.body) return;
    this.body.innerHTML = '<div class="drawer-loading">Loading cart...</div>';

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      this.body.innerHTML = '<div class="drawer-loading">Error loading cart.</div>';
    }
  }

  renderCart(cart) {
    const count = cart.item_count || 0;

    // Update count displays
    if (this.countEl) this.countEl.textContent = count;
    this.updateBadge(count);

    if (count === 0) {
      this.body.innerHTML = `
        <div class="cart-empty-msg" style="padding: 3rem 1rem; text-align: center;">
          <svg width="48" height="48" fill="none" stroke="rgba(0,0,0,0.2)" stroke-width="1.5" viewBox="0 0 24 24" style="margin: 0 auto 1rem;"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path><line x1="3" y1="6" x2="21" y2="6"></line><path d="M16 10a4 4 0 0 1-8 0"></path></svg>
          <p style="font-weight: 600; color: #111; margin-bottom: 0.5rem;">Your cart is empty</p>
          <p style="font-size: 0.85rem; color: #888; margin-bottom: 1.5rem;">Add some products to see them here.</p>
          <a href="/collections/all" class="btn-drawer-checkout" style="display: inline-block; width: auto; padding: 0.6rem 1.5rem;">Explore Catalog</a>
        </div>`;
      if (this.footer) this.footer.style.display = 'none';
      return;
    }

    if (this.footer) this.footer.style.display = 'block';
    if (this.subtotalEl) this.subtotalEl.textContent = this.formatMoney(cart.total_price);

    // Render Items
    this.body.innerHTML = cart.items.map(item => `
      <div class="drawer-item" data-key="${item.key}">
        <img class="drawer-item-img" src="${item.image ? item.image + '&width=140' : ''}" alt="${item.title}">
        <div class="drawer-item-info">
          <a href="${item.url}" class="drawer-item-title">${item.product_title}</a>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<span class="drawer-item-variant">${item.variant_title}</span>` : ''}
          <span class="drawer-item-price">${this.formatMoney(item.final_line_price)}</span>
          <div class="drawer-item-qty">
            <button type="button" class="drawer-qty-btn qty-minus" data-key="${item.key}" data-qty="${item.quantity - 1}">&minus;</button>
            <span class="drawer-qty-num">${item.quantity}</span>
            <button type="button" class="drawer-qty-btn qty-plus" data-key="${item.key}" data-qty="${item.quantity + 1}">&plus;</button>
          </div>
        </div>
        <button type="button" class="drawer-item-remove" data-key="${item.key}" aria-label="Remove item">&times;</button>
      </div>
    `).join('');

    this.bindDrawerItemEvents();
  }

  bindDrawerItemEvents() {
    this.body.querySelectorAll('.drawer-qty-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const key = e.currentTarget.dataset.key;
        const qty = parseInt(e.currentTarget.dataset.qty);
        this.changeQuantity(key, qty);
      });
    });

    this.body.querySelectorAll('.drawer-item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const key = e.currentTarget.dataset.key;
        this.changeQuantity(key, 0);
      });
    });
  }

  async changeQuantity(key, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      });
      const cart = await res.json();
      this.renderCart(cart);
    } catch (err) {
      console.error('Failed to change quantity:', err);
    }
  }

  updateBadge(count) {
    if (!this.badgeEl) return;
    this.badgeEl.textContent = count;
    if (count > 0) {
      this.badgeEl.classList.remove('hidden');
    } else {
      this.badgeEl.classList.add('hidden');
    }
  }

  bindGlobalFormSubmit() {
    // Intercept clicks in capture phase to ensure option/color/customizer buttons never trigger form submit
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;

      const isAddBtn = btn.getAttribute('name') === 'add' ||
                       btn.classList.contains('product-add-btn') ||
                       btn.classList.contains('tp-btn-add') ||
                       btn.classList.contains('quick-add-btn') ||
                       btn.classList.contains('customfly-add-to-cart');

      const isInsideOptions = btn.closest(
        '.variant-picker, .tp-variant-picker, .tp-customfly-container, .product-block-app, .option-fieldset, .tp-option-fieldset, [class*="imcst-"], [class*="customfly-"]'
      );

      if (isInsideOptions && !isAddBtn) {
        if (!btn.getAttribute('type') || btn.getAttribute('type') === 'submit') {
          btn.setAttribute('type', 'button');
        }
      }
    }, true);

    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!form || !form.action || !form.action.includes('/cart/add')) return;

      // Verify submitter is an authentic Add to Cart trigger
      const submitter = e.submitter;
      if (submitter) {
        const isAddBtn = submitter.getAttribute('name') === 'add' ||
                         submitter.classList.contains('product-add-btn') ||
                         submitter.classList.contains('tp-btn-add') ||
                         submitter.classList.contains('quick-add-btn') ||
                         submitter.classList.contains('customfly-add-to-cart');

        const isInsideOptions = submitter.closest(
          '.variant-picker, .tp-variant-picker, .tp-customfly-container, .product-block-app, .option-fieldset, .tp-option-fieldset, [class*="imcst-"], [class*="customfly-"]'
        );

        if (isInsideOptions && !isAddBtn) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }
      }

      e.preventDefault();
      const submitBtn = (submitter && submitter.getAttribute('name') === 'add') ? submitter : (form.querySelector('[type="submit"]') || form.querySelector('.product-add-btn') || form.querySelector('.tp-btn-add'));
      const originalText = submitBtn ? submitBtn.innerHTML : '';

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span>Adding...</span>';
      }

      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        if (!res.ok) {
          const errData = await res.json();
          alert(errData.description || 'Could not add product to cart.');
          return;
        }

        // Open cart drawer & refresh
        if (window.themeCartDrawer) {
          window.themeCartDrawer.open();
        }
        await this.fetchCart();

      } catch (err) {
        console.error('AJAX Add to Cart failed:', err);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalText;
        }
      }
    });
  }
}

// Sticky Header Animation Helper
const initStickyHeader = () => {
  const header = document.querySelector('.header-wrapper');
  if (!header) return;

  let lastScroll = 0;
  window.addEventListener('scroll', () => {
    const currentScroll = window.pageYOffset;
    if (currentScroll <= 0) {
      header.classList.remove('scroll-up');
      return;
    }

    if (currentScroll > lastScroll && !header.classList.contains('scroll-down')) {
      // Scroll Down
      header.classList.remove('scroll-up');
      header.classList.add('scroll-down');
    } else if (currentScroll < lastScroll && header.classList.contains('scroll-down')) {
      // Scroll Up
      header.classList.remove('scroll-down');
      header.classList.add('scroll-up');
    }
    lastScroll = currentScroll;
  });
};

// Global Scroll Reveal Animation Observer
const initScrollReveal = () => {
  const revealElements = document.querySelectorAll('.reveal-fade, .reveal-slide-up, .reveal-scale');
  if (revealElements.length === 0) return;

  const observerOptions = {
    root: null,
    rootMargin: '0px',
    threshold: 0.1
  };

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('reveal-active');
        observer.unobserve(entry.target); // Animate once
      }
    });
  }, observerOptions);

  revealElements.forEach(el => observer.observe(el));
};

// Initialize Theme Components on Content Loaded
document.addEventListener('DOMContentLoaded', () => {
  // Initialize Cart Drawer Manager
  window.cartDrawerManager = new CartDrawerManager();

  // Initialize Cart Drawer UI
  window.themeCartDrawer = new ThemeDrawer('cart-drawer', '.cart-drawer-trigger');

  // Initialize Sticky Navigation Behavior
  initStickyHeader();

  // Initialize scroll animation observer
  initScrollReveal();
});
